<template>
  <div
    v-if="loadingFilterSet"
    class="tw:flex tw:h-full tw:items-center tw:justify-center"
  >
    <BaseSpinner
      class="tw:size-[30px]"
      data-testid="loading-filter-set-spinner"
    />
    <div class="tw:flex tw:mt-2">
      {{ getLocaleText("filter_set_input_select.loading_filter_set_details") }}
    </div>
  </div>
  <BaseInputSelect
    v-else
    v-model="filterSet"
    :aria-label="getLocaleText('filter_sets.label')"
    class="tw:mr-2 tw:w-full"
    clearable
    data-testid="filter-set-input-select"
    :loading="loading"
    new-design
    option-label="label"
    option-value="id"
    :options="filteredFilterSetsList"
    :placeholder="
      getLocaleText('filter_set_input_select.select_filter_set_placeholder')
    "
    use-input
    @blur="restorePreviousFilterSetIfBlank"
    @filter="filterFilterSets"
    @focus="clearLocalFilterSet"
    @update:model-value="updateFilterSet"
  />
</template>

<script lang="ts" setup>
import { onMounted, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import { BaseInputSelect, BaseSpinner } from "shared/components/base";
import { getFilterSet, getFilterSets } from "shared/services/filterSet";
import type { FilterSet, Nullable } from "shared/types";

const filterSet = defineModel<Nullable<FilterSet>>({
  required: true,
});

const loading = ref<boolean>(false);
const loadingFilterSet = ref<boolean>(false);
const previousFilterSet = ref<Nullable<FilterSet>>(null);
const filterSetsList = ref<FilterSet[]>([]);
const filteredFilterSetsList = ref<FilterSet[]>([]);

const emit = defineEmits<{
  "update:filterSet": [Nullable<FilterSet>];
}>();

function filterFilterSets(query: string) {
  if (!query) {
    filteredFilterSetsList.value = filterSetsList.value;

    return;
  }

  filteredFilterSetsList.value = filterSetsList.value.filter(
    (localFilterSet: FilterSet) =>
      localFilterSet.label.toLowerCase().includes(query.toLowerCase())
  );
}

function clearLocalFilterSet() {
  previousFilterSet.value = filterSet.value;
  filterSet.value = null;
}

function restorePreviousFilterSetIfBlank() {
  if (!filterSet.value) {
    filterSet.value = previousFilterSet.value;
  }

  previousFilterSet.value = null;
}

async function updateFilterSet(localFilterSet: Nullable<FilterSet>) {
  if (!localFilterSet) {
    emit("update:filterSet", null);

    return;
  }

  const loadedFilterSet = await loadFilterSetById(localFilterSet.id);

  emit("update:filterSet", loadedFilterSet);
}

async function loadFilterSetById(filterSetId?: number) {
  loadingFilterSet.value = true;

  if (!filterSetId) {
    loadingFilterSet.value = false;

    return null;
  }

  try {
    const response = await getFilterSet(filterSetId);

    return response.data;
  } catch (error: unknown) {
    ErrorDialog(
      getLocaleText("filter_set_input_select.load_filter_set_error"),
      error
    );
  } finally {
    loadingFilterSet.value = false;
  }

  return null;
}

async function loadFilterSets() {
  loading.value = true;

  let response: FilterSet[] = [];

  try {
    response = await getFilterSets();
  } catch (error: unknown) {
    ErrorDialog(
      getLocaleText("filter_set_input_select.load_filter_sets_error"),
      error
    );
  } finally {
    loading.value = false;
  }

  return response;
}

function setFilterSet(localFilterSet: Nullable<FilterSet>) {
  filterSet.value = localFilterSet;
}

onMounted(async () => {
  filterSetsList.value = await loadFilterSets();

  filteredFilterSetsList.value = filterSetsList.value;
});

defineExpose({
  loadingFilterSet,
  setFilterSet,
});
</script>
